import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import BlogContent from "@/components/blog/BlogContent";
import ContentBlockRenderer from "@/components/blog/ContentBlockRenderer";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Share2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface Post {
  id: string;
  title: string;
  slug: string;
  content_html: string;
  content_json: any;
  excerpt: string | null;
  cover_image_url: string | null;
  cover_image_alt: string | null;
  published_at: string | null;
  author_name: string;
  seo_keywords: string | null;
  categories?: { name: string; slug: string } | null;
}

interface RelatedPost {
  slug: string;
  title: string;
  cover_image_url: string | null;
}

const BlogPost = () => {
  const { slug } = useParams<{ slug: string }>();
  const { toast } = useToast();
  const [post, setPost] = useState<Post | null>(null);
  const [related, setRelated] = useState<RelatedPost[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!slug) return;
    const fetchPost = async () => {
      setLoading(true);
      const { data } = await supabase
        .from("posts")
        .select("id, title, slug, content_html, content_json, excerpt, cover_image_url, cover_image_alt, published_at, author_name, seo_keywords, category_id, categories(name, slug)")
        .eq("slug", slug)
        .eq("status", "published")
        .maybeSingle();

      setPost(data as unknown as Post | null);

      if (data?.category_id) {
        const { data: relData } = await supabase
          .from("posts")
          .select("slug, title, cover_image_url")
          .eq("status", "published")
          .eq("category_id", data.category_id)
          .neq("id", data.id)
          .limit(3);
        setRelated(relData || []);
      }
      setLoading(false);
    };
    fetchPost();
  }, [slug]);

  const handleShare = async () => {
    await navigator.clipboard.writeText(window.location.href);
    toast({ title: "Link copiado!", description: "Compartilhe com seus amigos." });
  };

  if (loading) {
    return (
      <section className="section-shell">
        <div className="mx-auto max-w-3xl animate-pulse space-y-6">
          <div className="h-6 w-32 rounded bg-muted" />
          <div className="h-10 w-3/4 rounded bg-muted" />
          <div className="aspect-[16/9] rounded-2xl bg-muted" />
          <div className="space-y-3">
            <div className="h-4 w-full rounded bg-muted" />
            <div className="h-4 w-5/6 rounded bg-muted" />
            <div className="h-4 w-4/6 rounded bg-muted" />
          </div>
        </div>
      </section>
    );
  }

  if (!post) {
    return (
      <section className="section-shell text-center py-20">
        <h1 className="text-2xl font-semibold text-foreground mb-4">Artigo não encontrado</h1>
        <Link to="/blog">
          <Button variant="outline">Voltar ao Blog</Button>
        </Link>
      </section>
    );
  }

  const formattedDate = post.published_at
    ? new Date(post.published_at).toLocaleDateString("pt-BR", { day: "2-digit", month: "long", year: "numeric" })
    : null;

  return (
    <article className="section-shell">
      <div className="mx-auto max-w-3xl">
        <Link to="/blog" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-primary transition-colors mb-6">
          <ArrowLeft className="h-4 w-4" /> Voltar ao Blog
        </Link>

        <header className="mb-8 space-y-4">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            {post.categories?.name && (
              <span className="rounded-full bg-primary/10 px-2 py-0.5 font-medium text-primary">{post.categories.name}</span>
            )}
            {formattedDate && <span>{formattedDate}</span>}
            <span>• {post.author_name}</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-foreground leading-tight">{post.title}</h1>
          {post.excerpt && <p className="text-lg text-muted-foreground">{post.excerpt}</p>}
        </header>

        {post.cover_image_url && (
          <div className="mb-10 overflow-hidden rounded-2xl">
            <img
              src={post.cover_image_url}
              alt={post.cover_image_alt || post.title}
              className="w-full object-cover"
            />
          </div>
        )}

        {(post.content_json as any)?.content_blocks ? (
          <ContentBlockRenderer blocks={(post.content_json as any).content_blocks} />
        ) : (
          <BlogContent html={post.content_html} />
        )}

        <div className="mt-10 flex items-center justify-between border-t border-border pt-6">
          <Button variant="outline" size="sm" onClick={handleShare}>
            <Share2 className="mr-2 h-4 w-4" /> Compartilhar
          </Button>
          <Link to="/contato#apoie">
            <Button size="sm" className="rounded-full">Apoie o IESSA</Button>
          </Link>
        </div>

        {related.length > 0 && (
          <div className="mt-12">
            <h2 className="mb-4 text-xl font-semibold text-foreground">Posts relacionados</h2>
            <div className="grid gap-4 sm:grid-cols-3">
              {related.map((r) => (
                <Link key={r.slug} to={`/blog/${r.slug}`} className="group card-ambient overflow-hidden">
                  <div className="aspect-[16/9] bg-muted overflow-hidden">
                    {r.cover_image_url ? (
                      <img src={r.cover_image_url} alt={r.title} className="h-full w-full object-cover transition-transform group-hover:scale-105" loading="lazy" />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center text-2xl text-primary/30">📝</div>
                    )}
                  </div>
                  <p className="p-3 text-sm font-medium text-foreground group-hover:text-primary transition-colors line-clamp-2">{r.title}</p>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </article>
  );
};

export default BlogPost;
